import { ButtonV2, Form } from 'components';
import { ModalControllerStepsContext } from 'hocs/withContext';
import Program from 'models/Program';
import Section from 'models/Section';
import React, { useContext, useEffect, useState } from 'react';

interface Step2Props {
  program: Program;
  onUpdateProgram: (updatedProgram: Program) => void;
  onClose: (program: Program | null) => void;
}

interface FormValues {
  registration_capacity?: number;
  ratio_numerator?: number;
  sections?: string[];
  standalone?: boolean;
}

const Step2: React.FC<Step2Props> = ({ program, onUpdateProgram, onClose }) => {
  const { prevStep, nextStep } = useContext(ModalControllerStepsContext);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const isSubmitDisabled = false;

  const [sections, setSections] = useState<Section[]>([]);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    req.rooms().then(rooms => setSections(rooms));
  }, []);

  const updateFormValues = (values: FormValues) => {
    const disableSections = !!values.standalone;
    setStandalone(disableSections);

    const selectedSections = disableSections
      ? []
      : (values.sections || []).map(id => sections.find(s => s.id === id)).filter((s): s is Section => s !== undefined);

    onUpdateProgram({
      ...program,
      sections: selectedSections,
      standalone: disableSections,
      registration_capacity: Number(values.registration_capacity),
      ratio_numerator: Number(values.ratio_numerator)
    });
  };

  const submit = async (values: FormValues) => {
    if (!values.standalone && (values.sections || []).length === 0) {
      setErrors({
        ...errors,
        standalone: 'Please select at least one room or disable the room assignment.'
      });
      Actions.showFlash('Please select at least one room or disable the room assignment.', 'alert');
      return;
    }

    setIsLoading(true);

    const payload = {
      program: {
        ...program,
        sections: values.standalone ? [] : program.sections || [],
        program_sessions_attributes: program.program_sessions
      }
    };

    try {
      const response = await req.createProgram(payload);
      const updatedProgram = {
        ...response.program,
        // Backend always responds with sections as an empty array
        sections: program.sections || []
      };

      if (!values.standalone) {
        await req.batchUpdateProgramRooms({
          program_id: updatedProgram.id || '',
          section_ids: updatedProgram.sections?.map(s => s.id) || []
        });
      }

      setIsLoading(false);
      onUpdateProgram(updatedProgram);

      nextStep();
    } catch (err) {
      setIsLoading(false);
      const formErrors = (err as any).response?.data?.form_errors;

      if (formErrors) {
        setErrors(formErrors);
      } else {
        Actions.reportError('There was an error creating the program. Please try again.', err as Error);
      }
    }
  };

  return (
    <div className="modal__wrapper">
      <div className="modal__header">
        <ButtonV2 icon iconName="back" className="modal__header-back" onClick={prevStep} disabled={isLoading} />
        <span className="modal__header-title">Set Capacity and Ratios</span>
        <span className="modal__header-steps">
          <span>Step 2</span> / 2
        </span>
      </div>
      <div className="modal__container">
        <Form
          errors={errors}
          isLoading={isLoading}
          validateOn="submit"
          onSubmit={submit}
          onInit={updateFormValues}
          onChange={updateFormValues}
        >
          <div className="form__row">
            <Form.Input
              name="registration_capacity"
              label="Max Program Capacity"
              placeholder="Program Capacity"
              type="number"
              min={0}
              defaultValue={program.registration_capacity}
            />
          </div>

          <div className="form__row">
            <div className="form-input__field">
              <label className="form__label">Student : Staff Ratio</label>
              <div className="ratio-numerator">
                <span>Number of students</span>
                <Form.Input
                  name="ratio_numerator"
                  data-testid="ratio_numerator"
                  type="number"
                  min={0}
                  defaultValue={program.ratio_numerator}
                />
                <span>per staff</span>
              </div>
            </div>
          </div>

          <div className="form__row">
            <div className="form-input form-input">
              <label className="form__label">
                Assign Room To A Program
                <span className="form__asterisk">*</span>
              </label>
              <div className="form-input__field">
                {sections && (
                  <Form.Select
                    name="sections"
                    data-testid="sections"
                    title="Select a room(s)"
                    type="checkbox"
                    multipleSelectionText="rooms selected"
                    disabled={standalone}
                    defaultValue={program.sections?.map(s => s.id) || []}
                  >
                    {sections.map(r => (
                      <Form.Select.Item key={r.id} value={r.id} label={r.name} />
                    ))}
                  </Form.Select>
                )}
              </div>
            </div>
          </div>

          <div className="form__row">
            <div className="form-input__field standalone">
              <Form.Checkbox
                name="standalone"
                data-testid="standalone"
                label="This program does not need a room"
                defaultValue={program.standalone}
              />
            </div>
          </div>

          <div className="form__row form__row--actions">
            <ButtonV2 data-cy="cancel" secondary label="Cancel" disabled={isLoading} onClick={() => onClose(null)} />
            <Form.Submit data-cy="submit" label="Create Program" disabled={isLoading || isSubmitDisabled} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Step2;
